/**
 * Column resolution hook for the DataList component.
 *
 * Resolves column ordering, visibility, cell value extraction (with field
 * mapping and dot-notation path resolution), and computes column totals
 * for numeric columns that opt in via `total: true`.
 *
 * @see Spec sections 6.1-6.2
 * @see Plan section 3.7
 */

import { useMemo, useCallback } from 'react';

import type { ColumnConfig } from '../types';
import { applyColumnTotalOverrides } from '../utils/totals';

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

/**
 * Shape returned by `useColumnResolver`.
 */
export interface UseColumnResolverReturn {
  /** Visible columns sorted by `order`, with hidden columns filtered out. */
  visibleColumns: Array<{ key: string } & ColumnConfig>;

  /**
   * Extracts a cell value from a row given a column key.
   *
   * Applies `fieldMapping` first (if provided), then resolves the
   * resulting path using dot-notation traversal.
   */
  getCellValue: (row: Record<string, unknown>, columnKey: string) => unknown;

  /**
   * Footer totals for columns that have `total: true` (or a truthy `total`
   * object) AND a numeric type (`'number'` or `'currency'`).
   *
   * Keyed by column key; the value is the sum across all rows in `data`,
   * unless an authoritative override is supplied for that key (see the
   * `columnTotalOverrides` parameter), in which case the override wins.
   */
  columnTotals: Record<string, number>;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Resolves a dot-notation path against an arbitrary object.
 *
 * @example
 * ```ts
 * resolvePathValue({ user: { name: 'Alice' } }, 'user.name'); // 'Alice'
 * resolvePathValue({ a: null }, 'a.b'); // undefined
 * ```
 */
function resolvePathValue(obj: unknown, path: string): unknown {
  return path.split('.').reduce(
    (acc, key) =>
      acc == null ? undefined : (acc as Record<string, unknown>)[key],
    obj,
  );
}

/**
 * Returns `true` when a column's `total` property indicates that a total
 * should be computed.
 *
 * Accepts both `total: true` and `total: { label?: string; format?: string }`.
 */
function shouldComputeTotal(column: ColumnConfig): boolean {
  if (column.total === true) return true;
  if (typeof column.total === 'object' && column.total !== null) return true;
  return false;
}

/**
 * Returns `true` when the column type supports numeric totals.
 */
function isNumericType(column: ColumnConfig): boolean {
  const type = column.type ?? 'text';
  return type === 'number' || type === 'currency';
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Resolves column ordering, visibility, value extraction, and totals.
 *
 * @param columns      - Column definitions keyed by column name.
 * @param data         - Current data rows used for total computation.
 * @param fieldMapping - Optional mapping from column keys to API field
 *                       paths (dot notation). When omitted the column key
 *                       is used as-is to access row data.
 * @param columnTotalOverrides - Optional authoritative per-column totals
 *                       keyed by column key. A valid finite override
 *                       (including `0`) replaces the computed sum for that
 *                       column; absent/`undefined`/`NaN` entries fall through
 *                       to native summation.
 *
 * @returns Visible columns, a cell-value accessor, and footer totals.
 */
export function useColumnResolver(
  columns: Record<string, ColumnConfig>,
  data: Record<string, unknown>[],
  fieldMapping?: Record<string, string>,
  columnTotalOverrides?: Record<string, number>,
): UseColumnResolverReturn {
  // -----------------------------------------------------------------------
  // visibleColumns (memoised)
  // -----------------------------------------------------------------------

  /**
   * 1. Filter out columns with `hidden: true`.
   * 2. Sort by `order` (ascending). Columns without `order` come after
   *    ordered columns, in alphabetical key order.
   * 3. Map to `{ key, ...config }`.
   */
  const visibleColumns = useMemo(() => {
    return Object.entries(columns)
      .filter(([, col]) => !col.hidden)
      .sort((a, b) => {
        const orderA = a[1].order;
        const orderB = b[1].order;

        if (orderA !== undefined && orderB !== undefined) return orderA - orderB;
        if (orderA !== undefined) return -1;
        if (orderB !== undefined) return 1;

        return a[0].localeCompare(b[0]);
      })
      .map(([key, config]) => ({ key, ...config }));
  }, [columns]);

  // -----------------------------------------------------------------------
  // getCellValue
  // -----------------------------------------------------------------------

  /**
   * Extracts a cell value from a row:
   *
   * 1. Look up a mapped field path via `fieldMapping[columnKey]`.
   * 2. If no mapping exists, fall back to `columnKey` itself.
   * 3. Resolve the path using dot-notation traversal.
   */
  const getCellValue = useCallback(
    (row: Record<string, unknown>, columnKey: string): unknown => {
      const fieldPath = fieldMapping?.[columnKey] ?? columnKey;
      return resolvePathValue(row, fieldPath);
    },
    [fieldMapping],
  );

  // -----------------------------------------------------------------------
  // columnTotals (memoised)
  // -----------------------------------------------------------------------

  /**
   * For each visible column that:
   *   - has `total: true` (or a truthy `total` object), AND
   *   - has a numeric type (`'number'` or `'currency'`)
   *
   * Sum all cell values across `data` using `getCellValue`, then overlay any
   * authoritative `columnTotalOverrides` (override wins per key).
   */
  const columnTotals = useMemo(() => {
    const totals: Record<string, number> = {};

    for (const col of visibleColumns) {
      if (!shouldComputeTotal(col) || !isNumericType(col)) {
        continue;
      }

      let sum = 0;
      for (const row of data) {
        const raw = getCellValue(row, col.key);
        const num = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isNaN(num)) {
          sum += num;
        }
      }

      totals[col.key] = sum;
    }

    return applyColumnTotalOverrides(totals, columnTotalOverrides);
  }, [visibleColumns, data, getCellValue, columnTotalOverrides]);

  // -----------------------------------------------------------------------
  // Return
  // -----------------------------------------------------------------------

  return {
    visibleColumns,
    getCellValue,
    columnTotals,
  };
}
